import { zodResolver } from '@hookform/resolvers/zod';
import {
  type EreRecipientDto,
  type NotificationReason,
  useNotificationControllerSendNotification,
} from 'korint-sdk';
import { Pencil, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Form } from '../../forms/form';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  useToast,
} from '../../ui';
import { EreRecipientFields } from './EreRecipientFields';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type RetryNotificationDialogProps = Readonly<{
  policyId: string;
  reason: NotificationReason;
  initialRecipient: EreRecipientDto;
}>;

/** Per-row resend of the same letter to whoever it went to, or to a corrected recipient. */
export function RetryNotificationDialog({
  policyId,
  reason,
  initialRecipient,
}: RetryNotificationDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  const recipientSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().trim().min(1, t('notifications.recipientFields.validation.required')),
        lastName: z.string().trim().min(1, t('notifications.recipientFields.validation.required')),
        email: z.string().trim().email(t('notifications.recipientFields.validation.email')),
        company: z.string().trim().optional(),
      }),
    [t],
  );

  const isInitialRecipientComplete = recipientSchema.safeParse(initialRecipient).success;
  const [isEditing, setIsEditing] = useState(!isInitialRecipientComplete);

  const form = useForm<EreRecipientDto>({
    defaultValues: initialRecipient,
    resolver: zodResolver(recipientSchema),
  });

  const { mutate: sendNotification, isPending } = useNotificationControllerSendNotification({
    mutation: {
      onSuccess: () => {
        toast({ title: t('notifications.retry.toast.success') });
        setOpen(false);
      },
      onError: () => {
        toast({ variant: 'destructive', title: t('notifications.retry.toast.error') });
      },
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen);
        if (nextOpen) {
          form.reset(initialRecipient);
          setIsEditing(!isInitialRecipientComplete);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <RotateCcw className="h-4 w-4 mr-2" />
          {t('notifications.retry.button')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('notifications.retry.dialogTitle')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <Stack asChild>
            <form
              onSubmit={form.handleSubmit(recipient =>
                sendNotification({ data: { type: 'ERE', policyId, reason, recipient } }),
              )}
              noValidate
            >
              {isEditing ? (
                <EreRecipientFields control={form.control} />
              ) : (
                <Group align="start" justify="between" className="rounded-md border p-4">
                  <Stack gap={1}>
                    <Text size="sm" tone="muted">
                      {t('notifications.retry.currentRecipient')}
                    </Text>
                    <Text weight="medium">
                      {[initialRecipient.firstName, initialRecipient.lastName].join(' ')}
                    </Text>
                    {initialRecipient.company && <p>{initialRecipient.company}</p>}
                    <p>{initialRecipient.email}</p>
                  </Stack>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    {t('notifications.retry.modify')}
                  </Button>
                </Group>
              )}
              <DialogFooter>
                <Button type="submit" disabled={isPending}>
                  {isPending
                    ? t('notifications.retry.submitting')
                    : t('notifications.retry.submit')}
                </Button>
              </DialogFooter>
            </form>
          </Stack>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
